import { BILLING_STATUS_QUERY_KEY } from "@/services/api/billingService"
import {
  markUpgradeLinkOpened,
  refreshForPaidAccess,
} from "@/utils/upgradeSession"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"

export const PAID_ACCESS_POLL_MS = 3000
export const PAID_ACCESS_MAX_ATTEMPTS = 20

export type PaidAccessState = "waiting" | "ready" | "timeout"

/**
 * Waits for the Pilot group to reach the access token after a payment: only a
 * forced refresh that shows the paid group ends the wait, and that refresh
 * itself refetches user, trial and limits.
 */
export function useAwaitPaidAccess(
  enabled: boolean,
  stepUp = false,
): {
  state: PaidAccessState
  retry: () => void
} {
  const queryClient = useQueryClient()
  const [state, setState] = useState<PaidAccessState>("waiting")
  const [round, setRound] = useState(0)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    markUpgradeLinkOpened({ stepUp })

    const attempt = async (count: number) => {
      const paid = await refreshForPaidAccess({ stepUp })
      if (cancelled) return
      if (paid) {
        void queryClient.invalidateQueries({
          queryKey: BILLING_STATUS_QUERY_KEY,
        })
        setState("ready")
        return
      }
      if (count + 1 >= PAID_ACCESS_MAX_ATTEMPTS) {
        setState("timeout")
        return
      }
      timer = setTimeout(() => void attempt(count + 1), PAID_ACCESS_POLL_MS)
    }
    void attempt(0)

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [enabled, stepUp, round, queryClient])

  const retry = useCallback(() => {
    setState("waiting")
    setRound((value) => value + 1)
  }, [])
  return { state, retry }
}
